import { Box } from '@mui/system';
import type { GeographicOption } from '@openx/types/targeting/geographic.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { Fragment, type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParameterRemove } from '../../../utils/hooks/useParameterRemove.ts';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';

type GeographicItemsDefaultProps = {
  geographicOptions: [string, (() => JSX.Element | null) | null][];
  optionsNames: Record<string, string>;
  onRemoveParameter?: (opt: GeographicOption) => void;
} & TargetingDropdownSectionProps;

export const GeographicItemsDefault = ({
  geographicOptions,
  optionsNames,
  hideButtons,
  isDisabled,
  onOpenDrawerClick,
  onRemove,
  onRemoveParameter,
  optionsCount,
  disabledReasonMessage,
}: GeographicItemsDefaultProps) => {
  const { t } = useTranslation();

  const parameterRemoveHandler = useParameterRemove<GeographicOption>(onRemoveParameter, onRemove);

  const configuredKeys = useMemo(
    () => geographicOptions.filter(([, component]) => Boolean(component)).map(([key]) => key),
    [geographicOptions]
  );

  const hasNextConfigured = (key: string) => {
    const idx = configuredKeys.indexOf(key);
    return idx !== -1 && idx < configuredKeys.length - 1;
  };

  return (
    <>
      {geographicOptions.map(([key, component], index) => (
        <Fragment key={key}>
          <TargetingDropdownSection
            name={t(optionsNames[key])}
            hideButtons={hideButtons}
            isDisabled={isDisabled}
            isEmpty={!component}
            onOpenDrawerClick={() => onOpenDrawerClick?.(key)}
            onRemove={parameterRemoveHandler(key as GeographicOption)}
            hasOptionsSelected={!!optionsCount && optionsCount > 0}
            disabledReasonMessage={disabledReasonMessage}
          >
            <Box key={`k-${index}`}>{component ? component() : null}</Box>
          </TargetingDropdownSection>

          {index < geographicOptions.length - 1 && (
            <DimensionChipLine
              targetingDimension={Criteria.ANY}
              showLabel={Boolean(component) && hasNextConfigured(key)}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};
